import logoUrl from "../../assets/sunwave-logo.svg";

interface Props {
  /** Pixel height of the logo. Width auto-scales to preserve aspect ratio. */
  height?: number;
  /** Inline style overrides (margin, etc.). */
  style?: React.CSSProperties;
  /** Optional override label. Falls back to "SunWave Spark". */
  alt?: string;
  /** Disable img loading and force the text fallback (used by tests). */
  forceFallback?: boolean;
}

/**
 * Renders the Sunwave logo from ``frontend/src/assets/sunwave-logo.svg``.
 *
 * The SVG is imported via Vite's standard asset URL import (works in
 * Vitest without needing the svgr plugin in the test pipeline). When
 * the asset is missing at build time, the import fails loudly — which
 * is what we want: silent fallbacks would mask a broken build.
 * Giovanni can replace the SVG file at any time; no code change is
 * needed downstream.
 *
 * For test environments that mock out the static asset import, an
 * ``onError`` handler swaps to the text fallback so the test page
 * still renders something visible.
 */
export function SunwaveLogo({
  height = 32,
  style,
  alt = "SunWave Spark",
  forceFallback = false,
}: Props) {
  if (forceFallback || !logoUrl) {
    return (
      <span
        data-testid="sunwave-logo-fallback"
        style={{
          fontWeight: 700,
          fontSize: height * 0.6,
          color: "#0a0e27",
          letterSpacing: "-0.01em",
          ...style,
        }}
      >
        SunWave Spark
      </span>
    );
  }
  return (
    <img
      src={logoUrl}
      alt={alt}
      data-testid="sunwave-logo"
      height={height}
      style={{ height, width: "auto", display: "block", ...style }}
      onError={(e) => {
        // Hide the broken image so a downstream caller's text fallback
        // (e.g., inside a Link wrapper) shows through.
        (e.currentTarget as HTMLImageElement).style.display = "none";
      }}
    />
  );
}
